import { Component, Story, Knobs } from './DocTypes';

const PlaygroundKnobs: Knobs = {
  multiple: {
    default: true,
    editor: 'checkbox',
  },
  disabled: {
    default: false,
    editor: 'checkbox',
  },
  accept: {
    default: '',
    editor: 'textbox',
  },
  buttonText: {
    default: '',
    editor: 'textbox',
  },
  clearButtonText: {
    default: 'Clear',
    editor: 'textbox',
  },
  multipleFilesCaption: {
    default: '{{fileCount}} files selected',
    editor: 'textbox',
  },
  selectionBehaviour: {
    editor: 'dropdown',
    editorOptions: ['append', 'overwrite', ''],
    default: '',
  },
  colourScheme: {
    editor: 'dropdown',
    editorOptions: ['light', 'dark', 'default', ''],
    default: '',
  },
  device: {
    editor: 'dropdown',
    editorOptions: ['mobile', 'desktop', ''],
    default: '',
  },
};

const PlaygroundStory: Story = {
  title: 'Playground',
  description: 'A tag-file-picker-button where you can change all the attributes',
  coreComponent_v3: {
    html: `<tag3-file-picker-button {{...knobs}}>
    </tag3-file-picker-button>`,
    knobs: PlaygroundKnobs,
  },
  reactWrapper_v3: {
    jsx: `<Tag3FilePickerButton{{...knobs}} />`,
    knobs: PlaygroundKnobs,
  },
};

const tooltipStory: Story = {
  title: 'Tooltip with selected files',
  description: 'A tag-file-picker-button with all selected files displayed in a tooltip',
  reactWrapper_v3: {
    jsx: `<Example/>`,
    parentJSX: `const Example = () => {
      const [data, setData] = React.useState([]);

      return (
        <>
          <Tag3FilePickerButton multiple
            className='tooltip-target'
            onFileChange={(e) => setData(e.detail.files.map(f => f.name)) }
            onClearClick={() => setData([]) }>
          </Tag3FilePickerButton>
          {data.length > 1 && <Tag3Tooltip
            tooltipFor='.tooltip-target'
            width='100px'
            background='light'
            tippyProps={{arrow: false}}
            >
              {data.map(d => (<p key={d} style={{fontSize: "11px", margin: "0"}}>{d}</p>))}
          </Tag3Tooltip>}
        </>
      );
    }`,
  },
};

export const TagFilePickerButtonDocs: Component = {
  type: 'component',
  name: 'file-picker-button',
  description: 'Renders a file picker button',
  mobileFriendly: false,
  popular: false,
  preRelease: '',
  props: [
    {
      name: 'filePickerId',
      default: 'file',
      description:
        'The id of the file input. This is important if there are multiple uploads on the page',
      type: 'string',
    },
    {
      name: 'multiple',
      description: 'Whether multiple files can be uploaded',
      type: 'boolean',
    },
    {
      name: 'buttonText',
      description: 'The default text to display on the button',
      type: 'string',
    },
    {
      name: 'clearButtonText',
      description: 'The text to display on the clear button',
      type: 'string',
      default: 'Clear',
    },
    {
      name: 'multipleFilesCaption',
      description:
        'The template text to display when files have been selected. {{fileCount}} is a token that will output the number of files selected',
      type: 'string',
      default: '{{fileCount}} files selected',
    },
    {
      name: 'disabled',
      description: 'Whether the component is disabled',
      type: 'boolean',
      default: 'false',
    },
    {
      name: 'accept',
      description: 'Specifies the types of files that the server accepts',
      type: 'string',
    },
    {
      name: 'selectionBehaviour',
      description: 'Set preferred selection behaviour.',
      type: 'append | overwrite',
      default: 'append',
    },
    {
      name: 'colourScheme',
      description:
        'Dark mode (theme) control. Default for normal browser driven control. Dark | Light to force respective styling regardless of browser setting.',
      type: 'dark | light | default',
      default: 'light',
    },
    {
      name: 'device',
      description: 'Set preferred styling theme for device type.',
      type: 'desktop | mobile',
      default: 'desktop',
    },
  ],
  reactPropOverrides: [],
  events: [
    {
      name: 'fileChange',
      description: 'raised when a file is selected',
      parameters: [
        {
          name: 'files',
          description: 'The files that are selected',
          type: 'FileList',
        },
      ],
    },
    {
      name: 'clearClick',
      description: 'Raised when the clear button is clicked',
      parameters: [],
    },
  ],
  methods: [
    {
      name: 'focusButton',
      description: 'Sets the focus to the file picker button',
      parameters: [],
      returnType: 'void',
    },
    {
      name: 'clear',
      description: 'Clears the current files that are selected',
      parameters: [],
      returnType: 'void',
    },
  ],
  stories: [PlaygroundStory, tooltipStory],
};
